import React from 'react';
import { Link, useParams } from 'react-router';
import { Badge } from '@rezi/ui/badge';
import { Card } from '@rezi/ui/card';
import { useDocById } from '../docsRegistry';
import { useDocCategories } from '../docsRegistry';
import DocContent from '../components/DocContent';
import MetadataPanel from '../components/MetadataPanel';

const getStatusColor = (status: string) => {
  switch (status.toLowerCase()) {
    case 'active':
      return 'green';
    case 'draft':
      return 'yellow';
    case 'archived':
      return 'gray';
    default:
      return 'blue';
  }
};

const DocPage = () => {
  const { docId } = useParams();
  const doc = useDocById(docId);
  const categories = useDocCategories();

  if (!doc) {
    return (
      <Card className="flex flex-col gap-3">
        <div className="text-text text-xl font-semibold">
          Document not found
        </div>
        <p className="text-sm text-gray-500">
          The document ID does not exist in the registry.
        </p>
        <nav className="text-sm text-gray-500">
          <Link className="text-rezi-blue-600 font-semibold" to="/">
            Docs
          </Link>
          <span className="px-2 text-gray-400">/</span>
          <span>Not found</span>
        </nav>
      </Card>
    );
  }

  const Content = doc.Content;

  const category = categories.find(
    (entry) => entry.id === doc.categoryPath.split('/')[0],
  );
  const visibility = doc.visibility.toLowerCase();

  return (
    <section className="flex flex-col gap-6">
      <nav className="text-sm text-gray-500">
        <Link className="text-rezi-blue-600 font-semibold" to="/">
          Docs
        </Link>
        {category && (
          <>
            <span className="px-2 text-gray-400">/</span>
            <Link
              className="text-rezi-blue-600 font-semibold"
              to={`/category/${category.id}`}
            >
              {category.title}
            </Link>
          </>
        )}
        <span className="px-2 text-gray-400">/</span>
        <span>{doc.title}</span>
      </nav>
      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_300px]">
        <Card className="flex flex-col gap-6">
          <div className="flex w-full flex-row items-center justify-between">
            <div className="flex flex-wrap items-baseline gap-3">
              <h1 className="text-text text-2xl font-semibold">{doc.title}</h1>
              <span
                className={`text-xs font-semibold tracking-[0.12em] uppercase ${
                  visibility === 'confidential'
                    ? 'text-red-500'
                    : 'text-gray-400'
                }`}
              >
                {visibility === 'internal' || visibility === 'confidential'
                  ? `${doc.visibility} ONLY`
                  : doc.visibility}
              </span>
            </div>
            <Badge
              label={doc.status}
              color={getStatusColor(doc.status)}
              size="small"
              allCaps
            />
          </div>

          <div className="space-y-4 text-sm leading-relaxed text-gray-700 [&_h1]:text-2xl [&_h1]:font-semibold [&_h2]:mt-6 [&_h2]:border-t [&_h2]:pt-6 [&_h2]:text-xl [&_h2]:font-semibold [&_h3]:inline [&_h3]:text-base [&_h3]:font-semibold [&_h3]:before:block [&_h3]:before:h-[10px] [&_h3]:before:content-[''] [&_h3]:after:mr-2 [&_h3]:after:content-[':'] [&_li]:mb-2 [&_p]:mb-2 [&_p]:inline [&_p]:text-base [&_ul]:list-disc [&_ul]:pl-5">
            <DocContent Content={Content} />
          </div>
        </Card>
        <MetadataPanel doc={doc} />
      </div>
    </section>
  );
};

export default DocPage;
